/**
 * WorkWise server sizes from the admin T-shirt sizes of the `workwise` product
 * (GET /products/sizes/), and their VM compute prices from
 * GET /workwise/size-prices/ (revision 7, R7-B; the generic product pricing is
 * no longer used for WorkWise).
 *
 * The size picked in step 2 sizes the APP server, and a dedicated DB and gateway
 * too (R3-G). Only the tier letter is sent; the backend resolves the VM size from
 * the same admin row.
 *
 * Node-safe: relative imports only, no import.meta (the mock layer uses it).
 */
import { WORKWISE_SKUS } from '../../config/workwise'
import type { WorkwiseSizePrices, WorkwiseSku } from '../../types/workwise'
import { readMaxUsers } from '../maxUsers'

/** The fields this module needs from a TShirtSizeConfig row. */
export interface AdminSizeConfigLike {
  product: number
  size: string
  resources: object
}

/** One admin T-shirt size, as shown on the size cards. */
export interface AdminSize {
  tier: WorkwiseSku
  /** storage_class, verbatim (e.g. "Standard B2s"). */
  vmSize: string
  vcpu: string
  ram: string
  storageGb: number | null
  /** os_type when the API exposes it, else null (the app server then defaults to Windows). */
  osType: string | null
  /** Revision 7 (R7-A): how many users can log in at this size; null when not set. */
  maxUsers: number | null
}

export type AdminSizes = Partial<Record<WorkwiseSku, AdminSize>>

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : value == null ? '' : String(value)
}

function positiveNumber(value: unknown): number | null {
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) && n > 0 ? n : null
}

/** The admin sizes of one product, keyed by tier. */
export function readAdminSizes(
  configs: readonly AdminSizeConfigLike[] | undefined,
  productId: number | undefined,
): AdminSizes {
  const sizes: AdminSizes = {}
  if (!configs || productId === undefined) return sizes
  for (const config of configs) {
    if (config.product !== productId) continue
    const tier = config.size as WorkwiseSku
    if (!WORKWISE_SKUS.includes(tier)) continue
    const r = config.resources as Record<string, unknown>
    sizes[tier] = {
      tier,
      vmSize: text(r.storage_class),
      vcpu: text(r.cpu_request),
      ram: text(r.memory_request),
      storageGb: positiveNumber(r.storage_gb ?? r.storage_size_gb),
      osType: text(r.os_type) || null,
      maxUsers: readMaxUsers(r.max_users),
    }
  }
  return sizes
}

/** The tiers that exist in the admin config, in S/M/L/XL order. */
export function availableTiers(sizes: AdminSizes): WorkwiseSku[] {
  return WORKWISE_SKUS.filter((tier) => sizes[tier] !== undefined)
}

/** Max users of a size: the admin size config, else the size-prices answer, else null ("—"). */
export function maxUsersOf(tier: WorkwiseSku, sizes: AdminSizes, prices?: WorkwiseSizePrices): number | null {
  return sizes[tier]?.maxUsers ?? readMaxUsers(prices?.sizes?.[tier]?.max_users)
}

/**
 * The step-2 "Est. monthly" of a size (R7-C): its VM compute with Azure Hybrid
 * Benefit from GET /workwise/size-prices/; null ("—") when not priced or not loaded.
 */
export function computeMonthlyOf(tier: WorkwiseSku, prices?: WorkwiseSizePrices): number | null {
  return positiveNumber(prices?.sizes?.[tier]?.monthly_compute_hybrid_benefit)
}

/**
 * The Azure VM size the backend derives from an admin storage_class (contract
 * section 8, B1): trim, collapse whitespace to "_", prefix "Standard_" when
 * missing; null when the result is not a valid size name.
 */
export function normalizeVmSize(storageClass: string): string | null {
  const collapsed = storageClass.trim().replace(/\s+/g, '_')
  if (!collapsed) return null
  const prefixed = collapsed.startsWith('Standard_') ? collapsed : `Standard_${collapsed}`
  return /^Standard_[A-Za-z0-9_]+$/.test(prefixed) ? prefixed : null
}

/** "$548.96", or "—" when unknown. */
export function formatMonthly(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value) || value <= 0) return '—'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(value)
}
